/**
 * useOpsRole — returns the current user's role + permissions for an ops
 * system (facilities, security, dining, …). Wraps the existing
 * `/api/ops/{system}/team/me` endpoint and exposes a cheap `can(perm)`
 * helper for UI gating.
 *
 * Usage:
 *   const { role, can, loading } = useOpsRole('facilities');
 *   {can('po_approve') && <Button>Approve PO</Button>}
 *
 * Super-admins always return `can(*) === true`. Non-members get null role
 * and every `can()` returns false (the button is hidden, the server still
 * enforces — defence in depth).
 */
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';

export function useOpsRole(system) {
  const { data, isLoading } = useQuery({
    queryKey: ['ops-role', system],
    queryFn: () => api
      .get(`/ops/${system}/team/me`)
      .then((r) => r.data)
      // 403 means "not a member" — translate to a sentinel so the rest
      // of the hook works without surfacing as an error.
      .catch((e) => (e?.response?.status === 403 ? { role: null, permissions: [] } : Promise.reject(e))),
    enabled: !!system,
    staleTime: 60_000,  // membership rarely changes mid-session
  });

  const role = data?.role || null;
  const isSuper = !!data?.is_super;
  const permissions = data?.permissions || [];
  const can = (perm) => isSuper || permissions.includes(perm);

  return {
    role,
    isSuper,
    permissions,
    can,
    loading: isLoading,
    isMember: isSuper || role !== null,
  };
}
